import { useMutation } from '@tanstack/react-query';
import {
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  getAdditionalUserInfo,
  User,
} from 'firebase/auth';
import { auth } from 'src/config/firebase';
import ApiConfig from 'src/config/api-config';
import { IAuthSocialLoginPayload } from '../interfaces/auth';

export interface ISocialLoginResult {
  user: User;
  firstName: string;
  lastName: string;
  avatarUrl: string;
}

export function useAuthSocialLogin() {
  return useMutation({
    mutationFn: async (payload: IAuthSocialLoginPayload): Promise<ISocialLoginResult> => {
      const { providerName, rememberMe } = payload;

      // Set persistence if requested (optional for signup, but good for login)
      if (rememberMe !== undefined) {
        await setPersistence(
          auth,
          rememberMe ? browserLocalPersistence : browserSessionPersistence,
        );
      }

      const provider =
        providerName === 'google' ? new GoogleAuthProvider() : new OAuthProvider('apple.com');

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const additionalInfo = getAdditionalUserInfo(result);
      const oauthProfile = (additionalInfo?.profile || {}) as Record<string, any>;

      // 1. Try to extract first & last name from OAuth Profile (Google / Apple)
      let firstName = (
        oauthProfile.given_name ||
        oauthProfile.givenName ||
        oauthProfile.first_name ||
        ''
      ).trim();

      let lastName = (
        oauthProfile.family_name ||
        oauthProfile.familyName ||
        oauthProfile.last_name ||
        ''
      ).trim();

      // Apple may pass name as an object: { firstName: '...', lastName: '...' }
      if (!firstName && typeof oauthProfile.name === 'object' && oauthProfile.name) {
        firstName = (oauthProfile.name.firstName || oauthProfile.name.givenName || '').trim();
        lastName = (oauthProfile.name.lastName || oauthProfile.name.familyName || '').trim();
      }

      // 2. If name is still not completely split, try displayName or oauthProfile.name (as string)
      const rawFullName = (
        user.displayName ||
        (typeof oauthProfile.name === 'string' ? oauthProfile.name : '') ||
        ''
      ).trim();

      if ((!firstName || !lastName) && rawFullName) {
        const parts = rawFullName.split(/\s+/);
        if (!firstName && parts[0]) {
          firstName = parts[0];
        }
        if (!lastName && parts.length > 1) {
          lastName = parts.slice(1).join(' ');
        }
      }

      // If firstName has spaces and lastName is empty, split them
      if (firstName && !lastName && /\s+/.test(firstName)) {
        const parts = firstName.split(/\s+/);
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }

      // 3. Fallback from email if firstName or lastName is still empty
      const hasLetterRegex = /[\p{L}]/u;

      if (!firstName || !hasLetterRegex.test(firstName)) {
        const emailPrefix = (user.email || '').split('@')[0] || '';
        const cleanPrefix = emailPrefix.split(/[._-]/)[0] || '';
        const lettersOnly = cleanPrefix.replace(/[^\p{L}]/gu, '');
        firstName =
          lettersOnly.length > 0
            ? lettersOnly.charAt(0).toUpperCase() + lettersOnly.slice(1)
            : 'Trainer';
      }

      if (!lastName || !hasLetterRegex.test(lastName)) {
        const emailPrefix = (user.email || '').split('@')[0] || '';
        const emailParts = emailPrefix.split(/[._-]/);
        if (emailParts.length > 1) {
          const secondPartLetters = emailParts[1].replace(/[^\p{L}]/gu, '');
          if (secondPartLetters.length > 0) {
            lastName = secondPartLetters.charAt(0).toUpperCase() + secondPartLetters.slice(1);
          }
        }
      }

      if (!lastName || !hasLetterRegex.test(lastName)) {
        lastName = 'Trainer';
      }

      const email = (user.email || user.providerData?.[0]?.email || auth.currentUser?.email || '')
        .trim()
        .toLowerCase();
      const avatarUrl = user.photoURL || (oauthProfile.picture as string) || '';

      // Create or update user profile in Backend
      const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.CREATE_USER_PROFILE_API_HOST}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: user.uid,
          email: email,
          name: firstName.trim().slice(0, 50),
          surname: lastName.trim().slice(0, 50),
          avatar_url: avatarUrl || undefined,
          avatarUrl: avatarUrl || undefined,
        }),
      });

      // 409 means the user already exists, which is fine for login
      if (!response.ok && response.status !== 409) {
        console.error('Failed to create user profile in backend:', await response.text());
      }

      return {
        user,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        avatarUrl,
      };
    },
  });
}
